import React, { useEffect, useMemo, useState } from 'react';
import {
  ClaimKeyDatesView,
  DsarKeyDateField,
  KeyDateEntryView,
  KeyDateStatus,
  KeyDatesUpdateEntry,
  KeyDatesUpdatePayload,
  LocKeyDateField,
} from '../api/claims';

import './ClaimKeyDatesTimeline.css';
import { formatIsoDay } from './curation/format';

type SectionKey = 'loc' | 'dsar';

interface FieldConfig {
  field: LocKeyDateField | DsarKeyDateField;
  label: string;
  description?: string;
  hint?: string;
  allowSource?: boolean;
}

const LOC_FIELDS: FieldConfig[] = [
  { field: 'limitation', label: 'Limitation', description: 'Overall limitation for the claim.' },
  { field: 'locSent', label: 'LOC sent', description: 'Date the Letter of Claim was sent to the lender.' },
  {
    field: 'responseDue',
    label: 'LOC response due',
    description: 'Default is 8 weeks from LOC sent. Adjust if lender requests a different date.',
    allowSource: true,
  },
  { field: 'provisionalDecision', label: 'Provisional decision received', description: "Date of the lender's provisional decision letter." },
  { field: 'objectionDue', label: 'Objection deadline', description: 'Last day to object to the provisional decision.' },
  {
    field: 'lenderResponse',
    label: 'Lender response received',
    description: 'Track when the lender formally responds and link to the source correspondence.',
    allowSource: true,
  },
  {
    field: 'fosDeadlineDenied',
    label: 'FOS deadline (denied)',
    description: 'If the lender denies the complaint, record the FOS deadline (default is response date + 6 months).',
    allowSource: true,
  },
  {
    field: 'fosDeadlineNoResponse',
    label: 'FOS deadline (no response)',
    description: 'If no response is received, default is LOC sent + 5 months.',
  },
];

const DSAR_FIELDS: FieldConfig[] = [
  {
    field: 'limitation',
    label: 'DSAR limitation',
    description: 'Limitation period relating to DSAR obligations.',
  },
  {
    field: 'dsarSent',
    label: 'DSAR sent',
    description: 'Date the DSAR was issued (defaults to LOC date if shared).',
  },
  {
    field: 'due',
    label: 'DSAR due (30 days)',
    description: 'Standard DSAR deadline (DSAR sent + 30 days).',
    allowSource: true,
  },
  {
    field: 'dueWithExtension',
    label: 'DSAR due (with extension)',
    description: 'Revised deadline if an extension is agreed (DSAR sent + 90 days by default).',
    allowSource: true,
  },
  {
    field: 'extensionRequested',
    label: 'Extension requested',
    description: 'Track when an extension was requested/received along with the source reference.',
    allowSource: true,
  },
  {
    field: 'dsarResponse',
    label: 'DSAR response received',
    description: 'Record when the DSAR response is received and link to the evidence.',
    allowSource: true,
  },
  {
    field: 'breachLocSent',
    label: 'Breach LOC sent',
    description: 'Date the DSAR breach LOC was issued.',
  },
  {
    field: 'issueProceedings',
    label: 'Issue proceedings',
    description: 'Date proceedings were or should be issued for DSAR breach.',
  },
];

const SECTION_CONFIG: Record<SectionKey, { title: string; fields: FieldConfig[] }> = {
  loc: { title: 'Letter of Claim', fields: LOC_FIELDS },
  dsar: { title: 'DSAR', fields: DSAR_FIELDS },
};

const STATUS_LABELS: Record<KeyDateStatus, { label: string; className: string }> = {
  empty: { label: 'Not set', className: 'status-empty' },
  recorded: { label: 'Recorded', className: 'status-recorded' },
  overdue: { label: 'Overdue', className: 'status-overdue' },
  dueSoon: { label: 'Due soon', className: 'status-due-soon' },
  upcoming: { label: 'Upcoming', className: 'status-upcoming' },
};

const formatDate = (value?: string | null): string => {
  if (!value) {
    return 'Not set';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

const describeDeadline = (daysUntil?: number | null): string | null => {
  if (daysUntil === undefined || daysUntil === null) {
    return null;
  }
  if (daysUntil < 0) {
    return `${Math.abs(daysUntil)} day${Math.abs(daysUntil) === 1 ? '' : 's'} overdue`;
  }
  if (daysUntil === 0) {
    return 'Due today';
  }
  return `Due in ${daysUntil} day${daysUntil === 1 ? '' : 's'}`;
};

const buildUpdatePayload = (
  section: SectionKey,
  field: LocKeyDateField | DsarKeyDateField,
  entry: KeyDatesUpdateEntry | null,
): KeyDatesUpdatePayload => {
  if (section === 'loc') {
    return {
      loc: {
        [field as LocKeyDateField]: entry,
      } as Partial<Record<LocKeyDateField, KeyDatesUpdateEntry | null>>,
    };
  }
  return {
    dsar: {
      [field as DsarKeyDateField]: entry,
    } as Partial<Record<DsarKeyDateField, KeyDatesUpdateEntry | null>>,
  };
};

interface ClaimKeyDatesTimelineProps {
  claimId: string;
  data: ClaimKeyDatesView | null;
  loading: boolean;
  error: string | null;
  saving: boolean;
  onSave: (updates: KeyDatesUpdatePayload) => Promise<void>;
  onRefresh?: () => void | Promise<void>;
}

interface FieldCardProps {
  section: SectionKey;
  config: FieldConfig;
  entry: KeyDateEntryView;
  saving: boolean;
  onSave: (updates: KeyDatesUpdatePayload) => Promise<void>;
}

const KeyDateFieldCard: React.FC<FieldCardProps> = ({ section, config, entry, saving, onSave }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [formValue, setFormValue] = useState<string>(entry.manualValue || entry.value || '');
  const [notes, setNotes] = useState<string>(entry.notes || '');
  const [sourceId, setSourceId] = useState<string>(entry.sourceId || '');
  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => {
    setFormValue(entry.manualValue || entry.value || '');
    setNotes(entry.notes || '');
    setSourceId(entry.sourceId || '');
  }, [entry.manualValue, entry.value, entry.notes, entry.sourceId]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setLocalError(null);

    try {
      await onSave(
        buildUpdatePayload(section, config.field, {
          value: formValue || null,
          notes: notes ? notes : undefined,
          sourceId: sourceId ? sourceId : undefined,
        }),
      );
      setIsEditing(false);
    } catch (err: any) {
      setLocalError(err?.message || 'Failed to save key date');
    }
  };

  const handleClear = async () => {
    setLocalError(null);
    try {
      await onSave(buildUpdatePayload(section, config.field, null));
      setIsEditing(false);
    } catch (err: any) {
      setLocalError(err?.message || 'Failed to clear key date');
    }
  };

  const statusMeta = STATUS_LABELS[entry.status] ?? STATUS_LABELS.empty;
  const deadlineText = describeDeadline(entry.daysUntil);
  const showDefaultHint = entry.isManual && entry.defaultValue && entry.defaultValue !== entry.manualValue;
  const sourceBadge = entry.isManual ? 'Manual override' : entry.defaultValue ? 'Auto' : 'Unset';

  return (
    <div className={`key-date-card ${entry.status}`}>
      <div className="key-date-card-header">
        <div>
          <div className="key-date-label">{config.label}</div>
          {config.description && <div className="key-date-description">{config.description}</div>}
        </div>
        <span className={`status-chip ${statusMeta.className}`}>{statusMeta.label}</span>
      </div>

      {!isEditing && (
        <div className="key-date-body">
          <div className="key-date-value-row">
            <span className="key-date-value">{formatDate(entry.value)}</span>
            <span className={`source-chip ${entry.isManual ? 'manual' : entry.defaultValue ? 'auto' : 'unset'}`}>
              {sourceBadge}
            </span>
          </div>
          {deadlineText && <div className="key-date-deadline">{deadlineText}</div>}
          {showDefaultHint && (
            <div className="key-date-default">Default: {formatDate(entry.defaultValue)}</div>
          )}
          {entry.notes && <div className="key-date-notes">Note: {entry.notes}</div>}
          {entry.sourceId && <div className="key-date-source">Source: {entry.sourceId}</div>}
        </div>
      )}

      {isEditing && (
        <form className="key-date-form" onSubmit={handleSubmit}>
          <label className="key-date-form-field">
            <span>Date</span>
            <input
              type="date"
              value={formValue}
              onChange={(event) => setFormValue(event.target.value)}
              disabled={saving}
            />
          </label>
          <label className="key-date-form-field">
            <span>Notes</span>
            <textarea
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              placeholder="Optional context for this override"
              rows={2}
              disabled={saving}
            />
          </label>
          {config.allowSource && (
            <label className="key-date-form-field">
              <span>Source reference</span>
              <input
                type="text"
                value={sourceId}
                onChange={(event) => setSourceId(event.target.value)}
                placeholder="e.g. email ID, task ID"
                disabled={saving}
              />
            </label>
          )}

          {localError && <div className="key-date-error">{localError}</div>}

          <div className="key-date-form-actions">
            <button type="submit" className="primary" disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button type="button" onClick={() => setIsEditing(false)} disabled={saving}>
              Cancel
            </button>
            <button
              type="button"
              className="link"
              onClick={handleClear}
              disabled={saving || !entry.isManual}
            >
              Reset to default
            </button>
          </div>
        </form>
      )}

      {!isEditing && (
        <div className="key-date-actions">
          <button type="button" onClick={() => setIsEditing(true)} disabled={saving}>
            Edit
          </button>
        </div>
      )}
    </div>
  );
};

const SummaryBadge: React.FC<{ status: KeyDateStatus; count: number }> = ({ status, count }) => {
  const { label, className } = STATUS_LABELS[status];
  return (
    <div className={`summary-badge ${className}`}>
      <span className="summary-count">{count}</span>
      <span>{label}</span>
    </div>
  );
};

/** Dates the lender's letters set, recorded by a workflow or by staff. Computed defaults are never listed here. */
const LENDER_DEADLINES: Array<{ field: LocKeyDateField; label: string; deadline: boolean }> = [
  { field: 'provisionalDecision', label: 'Provisional decision received', deadline: false },
  { field: 'objectionDue', label: 'Objection deadline', deadline: true },
  { field: 'lenderResponse', label: 'Lender response received', deadline: false },
  { field: 'fosDeadlineDenied', label: 'FOS deadline', deadline: true },
];

const LenderDeadlines: React.FC<{ data: ClaimKeyDatesView }> = ({ data }) => {
  const rows = LENDER_DEADLINES.filter(({ field }) => data.loc?.[field]?.isManual && data.loc[field].value);
  if (!rows.length) return null;
  // Final review C12: a visible heading, styled like the timeline's column headings.
  return (
    <section aria-labelledby="lender-deadlines-title" style={{ display: 'flex', flexDirection: 'column', gap: '12px', paddingTop: '16px' }}>
      <h5 id="lender-deadlines-title" style={{ margin: 0, color: '#1f2937', fontSize: '1rem' }}>Lender deadlines</h5>
      {rows.map(({ field, label, deadline }) => {
        const entry = data.loc[field];
        const due = deadline ? describeDeadline(entry.daysUntil) : null;
        return (
          <div key={field} className="key-date-card recorded">
            <div className="key-date-card-header">
              <div className="key-date-label">{label}</div>
              {entry.extra?.estimated && <span className="source-chip unset">Estimated</span>}
            </div>
            <div className="key-date-body">
              <div className="key-date-value-row"><span className="key-date-value">{formatIsoDay(entry.value)}</span></div>
              {due && <div className="key-date-deadline">{due}</div>}
              {entry.notes && <div className="key-date-notes">{entry.notes}</div>}
            </div>
          </div>
        );
      })}
    </section>
  );
};

export const ClaimKeyDatesTimeline: React.FC<ClaimKeyDatesTimelineProps> = ({
  claimId,
  data,
  loading,
  error,
  saving,
  onSave,
  onRefresh,
}) => {
  const summaryCounts = useMemo(() => {
    if (!data) {
      return { overdue: 0, dueSoon: 0, upcoming: 0 };
    }
    return {
      overdue: data.counts?.overdue ?? data.summary.overdue.length,
      dueSoon: data.counts?.dueSoon ?? data.summary.dueSoon.length,
      upcoming: data.counts?.upcoming ?? data.summary.upcoming.length,
    };
  }, [data]);

  if (loading) {
    return (
      <div className="key-dates-card">
        <div className="key-dates-header">
          <h4>Key timeline</h4>
          <span className="key-dates-subtitle">Claim {claimId}</span>
        </div>
        <div className="key-dates-loading">Loading key dates…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="key-dates-card">
        <div className="key-dates-header">
          <h4>Key timeline</h4>
          <span className="key-dates-subtitle">Claim {claimId}</span>
        </div>
        <div className="key-dates-error">
          <div>{error}</div>
          {onRefresh && (
            <button type="button" onClick={onRefresh}>
              Retry
            </button>
          )}
        </div>
      </div>
    );
  }

  if (!data) {
    return null;
  }

  return (
    <div className="key-dates-card">
      <div className="key-dates-header">
        <div>
          <h4>Key timeline</h4>
          <span className="key-dates-subtitle">Claim {claimId}</span>
        </div>
        <div className="key-dates-header-actions">
          {onRefresh && (
            <button type="button" onClick={onRefresh} disabled={loading || saving}>
              Refresh
            </button>
          )}
        </div>
      </div>

      <LenderDeadlines data={data} />

      <div style={{ padding: '16px 0' }}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
          }}
        >
          <div className="key-date-card empty" style={{ cursor: 'default' }}>
            <div className="key-date-card-header">
              <div>
                <div className="key-date-label">
                  <span title="Financial Ombudsman Service" style={{ cursor: 'help', borderBottom: '1px dotted #999' }}>
                    FOS
                  </span>
                  {' '}deadline (no response)
                </div>
                <div className="key-date-description">If no response is received from the lender.</div>
              </div>
            </div>
            <div className="key-date-body">
              <div className="key-date-value-row">
                <span style={{ color: '#dc2626', fontWeight: 600, fontSize: '14px' }}>To be confirmed</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClaimKeyDatesTimeline;
